import { useEffect } from "react";
import { DEFAULT_DESCRIPTION, getSiteUrl, OG_IMAGE, SITE_NAME } from "../config/site";

const TWITTER_HANDLE = "";

const setMetaTag = (attr, value, isProperty = false) => {
  if (!value) return;
  const key = isProperty ? "property" : "name";
  let el = document.querySelector(`meta[${key}="${attr}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(key, attr);
    document.head.appendChild(el);
  }
  if (el.getAttribute("content") !== value) {
    el.setAttribute("content", value);
  }
};

const setMetaTagOg = (attr, value) => setMetaTag(attr, value, true);

const setLink = (rel, href, extra = {}) => {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
  Object.entries(extra).forEach(([key, value]) => {
    if (value) el.setAttribute(key, value);
  });
};

/**
 * SEO component: sets document title, meta description, canonical, Open Graph, and Twitter Card.
 * Use on every public page for proper indexing and sharing.
 */
const Seo = ({
  title,
  description = DEFAULT_DESCRIPTION,
  canonicalPath = "",
  image = OG_IMAGE,
  imageAlt,
  noindex = false,
  type = "website",
  siteName = SITE_NAME,
  publishedTime,
  modifiedTime,
  keywords,
}) => {
  const fullTitle = title ? `${title} | ${siteName}` : siteName;
  const base = getSiteUrl();
  const canonical = base && canonicalPath !== false ? `${base}${canonicalPath || ""}` : null;
  const imageUrl = image && base ? (image.startsWith("http") ? image : `${base}${image}`) : null;
  useEffect(() => {
    document.title = fullTitle;
    document.documentElement.lang = "en-IN";

    setMetaTag("description", description);
    setMetaTag("robots", noindex ? "noindex, nofollow" : "index, follow");
    setMetaTag("author", "Weps Technology");
    if (keywords) setMetaTag("keywords", keywords);

    setLink("canonical", canonical);
    setLink("alternate", canonical, { hrefLang: "en-IN" });

    setMetaTagOg("og:title", fullTitle);
    setMetaTagOg("og:description", description);
    setMetaTagOg("og:type", type);
    setMetaTagOg("og:locale", "en_IN");
    if (siteName) setMetaTagOg("og:site_name", siteName);
    if (canonical) setMetaTagOg("og:url", canonical);
    if (imageUrl) {
      setMetaTagOg("og:image", imageUrl);
      setMetaTagOg("og:image:alt", imageAlt || title || siteName);
      setMetaTagOg("og:image:width", "1280");
      setMetaTagOg("og:image:height", "720");
    }
    if (type === "article") {
      setMetaTagOg("article:published_time", publishedTime);
      setMetaTagOg("article:modified_time", modifiedTime || publishedTime);
      setMetaTagOg("article:author", "Weps Technology");
      setMetaTagOg("article:section", "Money habits");
    }

    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:title", fullTitle);
    setMetaTag("twitter:description", description);
    if (imageUrl) setMetaTag("twitter:image", imageUrl);
    if (TWITTER_HANDLE) setMetaTag("twitter:site", TWITTER_HANDLE);
  }, [
    fullTitle,
    description,
    noindex,
    canonical,
    imageUrl,
    imageAlt,
    type,
    siteName,
    publishedTime,
    modifiedTime,
    keywords,
    title,
  ]);

  return null;
};

export default Seo;
export { SITE_NAME as DEFAULT_SITE_NAME, DEFAULT_DESCRIPTION, OG_IMAGE as DEFAULT_IMAGE };
